// Gems: the Market's real-money currency, shaped like the reference game's
// (pokeidle-calibration docs/04, sections 5-6).
//
// - Gems are never given away: no drops, quests or prizes. They are bought
//   (1 gem = 1 cent) and move only between players, through Market sales
//   priced in gems. Each wallet keeps two parts: gems bought, and gems earned
//   from sales. Spending takes bought gems first. Only earned gems cash out,
//   with no hold (Cole 2026-10-20: card risk is capped at purchase instead), at least
//   GEM_WITHDRAW_MIN at a time, paid at GEM_USD less GEM_CASHOUT_SPREAD.
// - Anything earned can be listed for coins or gems, the seller's choice,
//   except what changes odds (charms, and drop and catch relics): coins only.
//   Things bought with diamonds or money stay account-bound, as for coins.
// - Our cut is taken in gems on every gem sale: 15% flat on items, held items,
//   relics and shrines; creatures pay a marginal rate like income tax (13% of
//   the part up to 500, 10% of 501-5,000, 7.5% above), so big sales stay here.
//   Fees round down, never above the rate; GEM_MIN_UNIT keeps every fee at least 1 gem.
// - Cash-out goes through Stripe Connect (server/gem-cash.ts): a one-time ID
//   check on Stripe's pages, then each request is paid as a Stripe transfer.
// - Final numbers: mythic-idle-design/legal/gems-final.md. Cash-out has its own
//   switch (kv 'gems-cashout-on', off): gems are bought and traded first, and
//   cash-out opens once the company, bank and Stripe Connect approval are in place.
// - The whole gem side sits behind an admin switch (kv 'gems-on', off): off,
//   no one sees gems, no gem listing can be made, no pack can be bought, and
//   gem listings already open stay hidden. It stays off until Stripe's OK and
//   the lawyer's sign-off (Cole, 2026-10-15: the Roblox model).
// - Balances live in their own table (server/gems.ts), never in the trainer
//   blob, and every change writes a gem ledger row in the same transaction.

/**
 * Item kinds that never trade for gems: odds boosts (Aura and Catch Charms, won in PvP) and charms. They trade
 * for coins only, so real money never buys better odds (Cole, 2026-10-15). There is no gems-for-coins exchange.
 */
export const GEM_BLOCKED_KINDS: readonly string[] = ['boost', 'charm'];
/** Whether an item of this kind may be listed for gems. */
export const gemListable = (itemKind: string | undefined) => !GEM_BLOCKED_KINDS.includes(itemKind ?? '');
/**
 * Relic stats that change odds: Charms (drop chance) and Talismans (catch chance). Like charms they trade for coins
 * only, and so does a creature that wears one (2026-10-20).
 */
export const GEM_BLOCKED_RELICS: readonly string[] = ['drop', 'catch'];
/** Whether a relic of this stat, or a creature wearing it, may be listed for gems. No relic: yes. */
export const relicGemListable = (stat: string | undefined) => !GEM_BLOCKED_RELICS.includes(stat ?? '');
/**
 * Lowest creature rating (shared/rating.ts, 0-100) that lists for gems: about the top 6% of fresh catches, so the
 * real-money board holds creatures worth browsing (Cole 2026-10-27). Legendaries always list; an Aura already rates
 * 30+ from its Aura points. Coin listings have no minimum.
 */
export const GEM_MIN_RATING = 30;
export const creatureGemRatingOk = (rating: number, rarity: string | undefined) => rarity === 'legendary' || rating >= GEM_MIN_RATING;

/** What one gem is worth, in US dollars, for display. */
export const GEM_USD = 0.01;
/** Lowest gem price per unit: from 8 gems the rounded-down fee is always at least 1 gem and at most 15%. */
export const GEM_MIN_UNIT = 8;
/** Trainer level to list for gems (buying is open to all): each alt account costs real hunting before it can sell. */
export const GEM_LIST_LEVEL = 40;
/** Highest gem price of one listing. */
export const GEM_MAX_LIST = 10_000_000;
/** Smallest cash-out: 1,000 gems ($9 paid). Stripe takes ~$2.27 per payout month; the spread plus the sale fees already taken cover it. */
export const GEM_WITHDRAW_MIN = 1000;
/** How long a card pack must go undisputed before it counts toward lifting the new-buyer cap. */
export const GEM_TRUST_AGE_MS = 14 * 86_400_000;
/** The cash-out spread: a cashed-out gem pays GEM_USD less this share. */
export const GEM_CASHOUT_SPREAD = 0.10;
/** Flat fee on items, held items, relics and shrines, in basis points. */
export const GEM_FEE_FLAT_BP = 1500;
/** Creature fee brackets: [top of the bracket in gems, rate in basis points]. */
export const GEM_FEE_BRACKETS: [number, number][] = [[500, 1300], [5000, 1000], [Infinity, 750]];

/** Our cut of one gem sale of `total` gems, rounded down, so never above the rate. */
export function gemFee(kind: string, total: number): number {
  const t = Math.max(0, Math.floor(total));
  if (kind !== 'creature') return Math.floor((t * GEM_FEE_FLAT_BP) / 10_000);
  let fee = 0, lo = 0;
  for (const [hi, bp] of GEM_FEE_BRACKETS) {
    if (t <= lo) break;
    fee += ((Math.min(t, hi) - lo) * bp) / 10_000;
    lo = hi;
  }
  return Math.floor(fee + 1e-9);
}

/** The effective fee on a sale, as a whole percent with one decimal, for the listing form. */
export const gemFeePct = (kind: string, total: number) => (total > 0 ? Math.round((gemFee(kind, total) / total) * 1000) / 10 : kind === 'creature' ? 13 : 15);

/**
 * New card buyers can spend at most GEM_NEW_DAILY_CENTS on gem packs in 24 hours. The cap lifts for good
 * once GEM_TRUST_CENTS of their packs are GEM_TRUST_AGE_MS old with no dispute: a stolen card is
 * worth at most a day's cap before it is caught, and a whale is only slowed for two weeks.
 */
export const GEM_NEW_DAILY_CENTS = 10_000;
export const GEM_TRUST_CENTS = 10_000;
/** Gem packs bought by card (server/store.ts), 1 gem = 1 cent, $5 and up. Bought gems never cash out. */
export interface GemPack { id: string; gems: number; cents: number }
export const GEM_PACKS: GemPack[] = [
  { id: 'g500', gems: 500, cents: 500 },
  { id: 'g1000', gems: 1000, cents: 1000 },
  { id: 'g2500', gems: 2500, cents: 2500 },
  { id: 'g5000', gems: 5000, cents: 5000 },
];
export const gemPackById = (id: unknown) => (typeof id === 'string' ? GEM_PACKS.find((p) => p.id === id) : undefined);

const usd = (d: number) => `$${d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
/** Dollars for a gem amount, for display: "$12.40". */
export const gemUsd = (gems: number) => usd(gems * GEM_USD);
/** Dollars a cash-out of `gems` pays, after the spread, rounded down to the cent. */
export const cashoutUsd = (gems: number) => usd(Math.floor(gems * GEM_USD * (1 - GEM_CASHOUT_SPREAD) * 100) / 100);
